import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { workspaceItemsApi } from '../../data/api/workspaceItems.api'
import type {
  CopyWorkspaceItemRequest,
  CreateWorkspaceFolderRequest,
  MoveWorkspaceItemRequest,
  RenameWorkspaceItemRequest,
  UploadWorkspaceFileChunkRequest,
} from '../models/WorkspaceItem'

export const workspaceMediaItemKeys = {
  all: ['workspaceItems'] as const,
  workspace: (workspaceId: string | undefined) => ['workspaceItems', workspaceId] as const,
  children: (workspaceId: string | undefined, parentId: string | null) =>
    ['workspaceItems', workspaceId, parentId] as const,
  preview: (workspaceId: string | undefined, itemId: string) =>
    ['workspaceItems', workspaceId, 'preview', itemId] as const,
  thumbnail: (workspaceId: string, itemId: string, contentVersion?: string | null) =>
    ['workspaceItems', workspaceId, 'thumbnail', itemId, contentVersion ?? null] as const,
  videoPreview: (workspaceId: string, itemId: string, contentVersion?: string | null) =>
    ['workspaceItems', workspaceId, 'video-preview', itemId, contentVersion ?? null] as const,
}

export function useWorkspaceItemThumbnail(
  workspaceId: string,
  itemId: string,
  contentVersion?: string | null,
) {
  return useWorkspaceThumbnail(workspaceId, itemId, contentVersion, 'thumbnail', true)
}

export function useWorkspaceVideoPreview(
  workspaceId: string,
  itemId: string,
  contentVersion: string | null | undefined,
  enabled: boolean,
) {
  return useWorkspaceThumbnail(workspaceId, itemId, contentVersion, 'video-preview', enabled)
}

function useWorkspaceThumbnail(
  workspaceId: string,
  itemId: string,
  contentVersion: string | null | undefined,
  kind: 'thumbnail' | 'video-preview',
  enabled: boolean,
) {
  const [imageUrl, setImageUrl] = useState<{ image: Blob; url: string }>()
  const thumbnail = useQuery({
    queryKey:
      kind === 'thumbnail'
        ? workspaceMediaItemKeys.thumbnail(workspaceId, itemId, contentVersion)
        : workspaceMediaItemKeys.videoPreview(workspaceId, itemId, contentVersion),
    queryFn: ({ signal }) =>
      kind === 'thumbnail'
        ? workspaceItemsApi.thumbnail(workspaceId, itemId)
        : workspaceItemsApi.videoPreview(workspaceId, itemId, signal),
    enabled,
    retry: false,
    staleTime: (query) =>
      kind === 'thumbnail' && !query.state.data?.pending ? Number.POSITIVE_INFINITY : 0,
    refetchInterval: (query) =>
      enabled &&
      query.state.status === 'success' &&
      query.state.data?.pending &&
      query.state.dataUpdateCount < 60
        ? 5000
        : false,
  })
  const image = thumbnail.data?.image

  useEffect(() => {
    if (!image) {
      setImageUrl(undefined)
      return
    }
    const objectUrl = URL.createObjectURL(image)
    setImageUrl({ image, url: objectUrl })
    return () => URL.revokeObjectURL(objectUrl)
  }, [image])

  return {
    ...thumbnail,
    data: image,
    metadata: thumbnail.data?.metadata,
    pending: thumbnail.data?.pending ?? false,
    url: imageUrl?.image === image ? imageUrl?.url : undefined,
  }
}

export function useWorkspaceItems(workspaceId: string, parentId: string | null) {
  return useQuery({
    queryKey: workspaceMediaItemKeys.children(workspaceId, parentId),
    queryFn: () => workspaceItemsApi.list(workspaceId, parentId).then((response) => response.data),
    enabled: Boolean(workspaceId),
  })
}

export function useCreateWorkspaceFolder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workspaceId,
      data,
    }: {
      workspaceId: string
      data: CreateWorkspaceFolderRequest
    }) => workspaceItemsApi.createFolder(workspaceId, data).then((response) => response.data),
    onSuccess: (_item, { workspaceId, data }) =>
      queryClient.invalidateQueries({
        queryKey: workspaceMediaItemKeys.children(workspaceId, data.parentId),
      }),
  })
}

export function useUploadWorkspaceFileChunk() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UploadWorkspaceFileChunkRequest) =>
      workspaceItemsApi.uploadFileChunk(request).then((response) => response.data),
    onSuccess: (_response, request) => {
      if (request.isFinalChunk) {
        return queryClient.invalidateQueries({
          queryKey: workspaceMediaItemKeys.children(request.workspaceId, request.parentId),
        })
      }
    },
  })
}

export function useDeleteWorkspaceItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ workspaceId, itemId }: { workspaceId: string; itemId: string }) =>
      workspaceItemsApi.delete(workspaceId, itemId),
    onSuccess: (_result, { workspaceId }) =>
      queryClient.invalidateQueries({ queryKey: workspaceMediaItemKeys.workspace(workspaceId) }),
  })
}

export function useMoveWorkspaceItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workspaceId,
      itemId,
      data,
    }: {
      workspaceId: string
      itemId: string
      sourceParentId: string | null
      data: MoveWorkspaceItemRequest
    }) => workspaceItemsApi.move(workspaceId, itemId, data),
    onSuccess: async (_response, { workspaceId, sourceParentId, data }) => {
      await Promise.all(
        [...new Set([sourceParentId, data.parentId])].map((parentId) =>
          queryClient.invalidateQueries({
            queryKey: workspaceMediaItemKeys.children(workspaceId, parentId),
          }),
        ),
      )
    },
  })
}

export function useCopyWorkspaceItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workspaceId,
      itemId,
      data,
      allowDuplicate,
    }: {
      workspaceId: string
      itemId: string
      sourceParentId: string | null
      allowDuplicate?: boolean
      data: CopyWorkspaceItemRequest
    }) => workspaceItemsApi.copy(workspaceId, itemId, data, allowDuplicate),
    onSuccess: async (_response, { workspaceId, sourceParentId, data }) => {
      await Promise.all(
        [...new Set([sourceParentId, data.parentId])].map((parentId) =>
          queryClient.invalidateQueries({
            queryKey: workspaceMediaItemKeys.children(workspaceId, parentId),
          }),
        ),
      )
    },
  })
}

export function useRenameWorkspaceItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workspaceId,
      itemId,
      data,
    }: {
      workspaceId: string
      itemId: string
      data: RenameWorkspaceItemRequest
    }) => workspaceItemsApi.rename(workspaceId, itemId, data),
    onSuccess: (_response, { workspaceId }) =>
      queryClient.invalidateQueries({ queryKey: workspaceMediaItemKeys.workspace(workspaceId) }),
  })
}
